/**
 * VideoSurface — the one place that decides WHICH video surface the review
 * panel shows: the processing placeholder, the native anchored-notes player,
 * the external embed, or the "no URL yet" empty state.
 *
 * Split out of ContentReviewPanel.tsx, where the same decision lived as a
 * four-branch ternary chain inside the JSX.
 */
import { Loader2, Video } from '../../../icons';
import { PlayerColumn, type CorsMode, type MediaController, type NotesSession } from './notes';
import { VideoEmbed } from './VideoEmbed';

const PROCESSING_ICON_SIZE = 28;
const PROCESSING_SPINNER_SIZE = 20;

const PROCESSING_TITLE = 'Processing video…';
const PROCESSING_BODY = 'The video is being prepared for review. This usually takes a minute.';
const EMPTY_BODY = 'No video URL yet. Add a content URL to preview here.';

/** Which surface the panel shows, in the order the panel resolves them. */
export type SurfaceKind = 'processing' | 'player' | 'embed' | 'empty';

export interface VideoSurfaceProps {
  /** Drive link whose S3 normalisation hasn't finished — wins over everything. */
  videoProcessing: boolean;
  /** True when the panel runs in anchored-notes mode. */
  notes: boolean;
  videoUrl?: string;
  /** Refreshed presigned URL from `useVideoUrlRefresh`; falls back to `videoUrl`. */
  nativeSrc: string;
  /** The native player's video failed for good. */
  videoLoadFailed: boolean;
  onRetryVideo: () => void;
  session: NotesSession;
  controller: MediaController;
  creatorName: string;
  seekFlashNonce: number;
  corsMode: CorsMode;
}

export function resolveSurfaceKind({
  videoProcessing,
  notes,
  videoUrl,
}: Pick<VideoSurfaceProps, 'videoProcessing' | 'notes' | 'videoUrl'>): SurfaceKind {
  if (videoProcessing) return 'processing';
  if (notes && videoUrl) return 'player';
  if (videoUrl) return 'embed';
  return 'empty';
}

function ProcessingSurface() {
  return (
    <div className="flex min-h-[240px] flex-col items-center justify-center gap-4 rounded-xl border border-border bg-muted p-8 text-center">
      <div className="flex size-16 items-center justify-center rounded-2xl bg-card ring-1 ring-ring">
        <Video size={PROCESSING_ICON_SIZE} className="text-muted-foreground" />
      </div>
      <div className="space-y-1.5">
        <h4 className="text-body-sm-semibold text-foreground">{PROCESSING_TITLE}</h4>
        <p className="text-caption-regular leading-relaxed text-muted-foreground">{PROCESSING_BODY}</p>
      </div>
      <Loader2 size={PROCESSING_SPINNER_SIZE} className="animate-spin text-(--brand-500)" />
    </div>
  );
}

function EmptySurface() {
  return (
    <div className="rounded-xl border border-dashed border-border bg-muted px-4 py-12 text-center text-body-sm-regular text-muted-foreground">
      {EMPTY_BODY}
    </div>
  );
}

export function VideoSurface({
  videoProcessing,
  notes,
  videoUrl,
  nativeSrc,
  videoLoadFailed,
  onRetryVideo,
  session,
  controller,
  creatorName,
  seekFlashNonce,
  corsMode,
}: VideoSurfaceProps) {
  const surfaceKind = resolveSurfaceKind({ videoProcessing, notes, videoUrl });
  const title = `${creatorName} content`;
  // `resolveSurfaceKind` only returns 'player'/'embed' when `videoUrl` is set,
  // but that guarantee lives in a function TS can't narrow through.
  const resolvedUrl = videoUrl ?? '';

  switch (surfaceKind) {
    case 'processing':
      return <ProcessingSurface />;
    case 'player':
      return (
        <PlayerColumn
          session={session}
          controller={controller}
          src={nativeSrc || resolvedUrl}
          loadFailed={videoLoadFailed}
          onRetryLoad={onRetryVideo}
          title={title}
          seekFlashNonce={seekFlashNonce}
          corsMode={corsMode}
        />
      );
    case 'embed':
      return <VideoEmbed url={resolvedUrl} title={title} />;
    case 'empty':
      return <EmptySurface />;
  }
}
